import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Star,
  Loader2,
  Check,
  XCircle,
  Trash2,
  ShieldCheck,
  MessageSquare,
} from 'lucide-react'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { apiGet } from '../lib/api'

interface ReviewRow {
  id: string
  productId: string | null
  productName: string | null
  productSku: string | null
  customerName: string
  customerCity: string
  rating: number
  title: string
  content: string
  image: string
  isVerified: boolean
  isPublished: boolean
  createdAt: string | null
}

interface ReviewsData {
  total: number
  page: number
  pageSize: number
  pages: number
  data: ReviewRow[]
}

type StatusFilter = '' | 'pending' | 'published'

export function Reviews() {
  const [status, setStatus] = useState<StatusFilter>('')
  const [page, setPage] = useState(1)
  const qc = useQueryClient()

  const { data, isLoading, isFetching } = useQuery<ReviewsData>({
    queryKey: ['admin-reviews', status, page],
    queryFn: () =>
      apiGet(
        `/api/admin/reviews?page=${page}${status ? `&status=${status}` : ''}`,
      ),
    placeholderData: (prev) => prev,
  })

  const moderate = useMutation({
    mutationFn: async ({ id, is_published }: { id: string; is_published: boolean }) => {
      const r = await fetch(`/api/admin/reviews/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_published }),
      })
      const j = await r.json()
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`)
      return j
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-reviews'] }),
  })

  const verifyMut = useMutation({
    mutationFn: async ({ id, is_verified }: { id: string; is_verified: boolean }) => {
      const r = await fetch(`/api/admin/reviews/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_verified }),
      })
      const j = await r.json()
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`)
      return j
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-reviews'] }),
  })

  const del = useMutation({
    mutationFn: async (id: string) => {
      const r = await fetch(`/api/admin/reviews/${id}`, { method: 'DELETE' })
      const j = await r.json()
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`)
      return j
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-reviews'] }),
  })

  function onDelete(r: ReviewRow) {
    if (!confirm(`¿Eliminar la reseña de ${r.customerName}?`)) return
    del.mutate(r.id)
  }

  return (
    <>
      <Topbar
        title="Reseñas y testimonios"
        subtitle={data ? `${data.total.toLocaleString('es-MX')} reseñas` : 'Cargando…'}
      />
      <div className="p-6">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {(
            [
              { v: '', label: 'Todas' },
              { v: 'pending', label: 'Pendientes' },
              { v: 'published', label: 'Publicadas' },
            ] as Array<{ v: StatusFilter; label: string }>
          ).map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => {
                setStatus(opt.v)
                setPage(1)
              }}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                status === opt.v
                  ? 'border-brand-500 bg-brand-50 text-brand-700'
                  : 'border-ink-200 bg-white text-ink-700 hover:bg-ink-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
          {isLoading && (
            <div className="grid place-items-center py-20 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
            </div>
          )}

          {data && data.data.length === 0 && (
            <div className="grid place-items-center py-16 text-center">
              <MessageSquare className="size-10 text-ink-300" />
              <p className="mt-3 text-sm font-medium text-ink-700">Sin reseñas</p>
            </div>
          )}

          {data && data.data.length > 0 && (
            <>
              {isFetching && (
                <div className="border-b border-ink-100 bg-ink-50 px-4 py-1.5 text-xs text-ink-500">
                  Actualizando…
                </div>
              )}
              <ul className="divide-y divide-ink-100">
                {data.data.map((r) => (
                  <li key={r.id} className="p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-ink-900">{r.customerName}</span>
                          <span className="text-xs text-ink-500">
                            · {r.customerCity || 'México'}
                          </span>
                          <div className="flex text-amber-500">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`size-3.5 ${i < r.rating ? 'fill-current' : ''}`}
                              />
                            ))}
                          </div>
                          {r.isVerified && (
                            <Badge variant="success">
                              <ShieldCheck className="size-3" /> Verificada
                            </Badge>
                          )}
                          {r.isPublished ? (
                            <Badge variant="info">Publicada</Badge>
                          ) : (
                            <Badge variant="warning">Pendiente</Badge>
                          )}
                        </div>
                        {r.title && (
                          <h4 className="mt-1 font-semibold text-ink-900">{r.title}</h4>
                        )}
                        <p className="mt-1 text-sm text-ink-700">{r.content}</p>
                        <div className="mt-2 text-xs text-ink-500">
                          {r.productSku ? (
                            <>
                              Sobre producto:{' '}
                              <span className="font-mono">{r.productSku}</span>{' '}
                              {r.productName && <>· {r.productName}</>}
                            </>
                          ) : (
                            <span>Testimonio general (home)</span>
                          )}
                          {r.createdAt && (
                            <> · {new Date(r.createdAt).toLocaleDateString('es-MX')}</>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {!r.isPublished ? (
                          <button
                            type="button"
                            onClick={() => moderate.mutate({ id: r.id, is_published: true })}
                            disabled={moderate.isPending}
                            className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                          >
                            <Check className="size-3.5" /> Aprobar
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => moderate.mutate({ id: r.id, is_published: false })}
                            disabled={moderate.isPending}
                            className="inline-flex items-center gap-1 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 hover:bg-ink-50 disabled:opacity-50"
                          >
                            <XCircle className="size-3.5" /> Despublicar
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() =>
                            verifyMut.mutate({ id: r.id, is_verified: !r.isVerified })
                          }
                          disabled={verifyMut.isPending}
                          className="inline-flex items-center gap-1 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 hover:bg-ink-50 disabled:opacity-50"
                        >
                          <ShieldCheck className="size-3.5" />
                          {r.isVerified ? 'Quitar verificada' : 'Marcar verificada'}
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(r)}
                          disabled={del.isPending}
                          className="inline-flex items-center gap-1 rounded-lg p-1.5 text-ink-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                          title="Eliminar"
                        >
                          <Trash2 className="size-4" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              {data.pages > 1 && (
                <div className="flex items-center justify-between border-t border-ink-200 bg-ink-50 px-4 py-3 text-sm">
                  <div className="text-ink-500">
                    Página <strong className="text-ink-900">{data.page}</strong> de{' '}
                    <strong className="text-ink-900">{data.pages}</strong>
                  </div>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                    >
                      Anterior
                    </button>
                    <button
                      type="button"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={page >= data.pages}
                      className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default Reviews
